'use client';
import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useForm, Path } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Tooltip } from 'react-tooltip';
import dynamic from 'next/dynamic';
import { addDays } from 'date-fns';
import { dayKeyOrInvalid } from '@/lib/date-format';
import { parseDateString } from '@/lib/rome-date';

import DatePicker from '../DatePicker';
import NotifyVillageAvailabilitySuccessModal from '../NotifyVillageAvailabilityModal/SuccessModal';
import Button from '../Button';
import QuantitieInput, { QuantityOption } from '../QuantitieInput';
import QuantitieInputRoot from '../QuantitieInput/Root';
import QuantitieInputBox from '../QuantitieInput/Box';
import VillageSideColumnShortlist from './VillageSideColumnShortlist';
import VillageSideColumnEmailModal from './VillageSideColumnEmailModal';
import FacebookIcon from '../Icons/Facebook';
import LinkedinIcon from '../Icons/Linkedin';
import TwitterIcon from '../Icons/Twitter';
import GlobalLoader from '../GlobalLoader';
import HelpIcon from '../Icons/Help';
import BookingReminder from '../BookingReminder';

import { cn } from '@/lib/utils';
import useVillageContext from '@/hooks/useVillageContext';
import useVillage from '@/hooks/useVillage';
import { createVillageBooking, getVillageBooking } from '@/services/village';
import {
  deleteStorageBooking,
  getStorageBooking,
  setStorageBooking,
} from '@/utils/helpers';

import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import {
  Village,
  VillageDeal,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';

import { IContactPageFormDictionary } from '@/dictionaries/pages/contacts/types';
import IVillageSideColumnDictionary from '@/dictionaries/components/villageSideColumn/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import IBookVillageDictionary from '@/dictionaries/pages/bookVillage/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import { useCurrency } from '@/contexts/currencyContext';
import { RiLoader2Fill } from 'react-icons/ri';
import { useLocale } from '@/hooks/useLocale';
import useVillageDates from '@/hooks/useVillageDates';
import HalfMoonIcon from '../Icons/HalfMoon';
import { formatLocalizedNumber } from '@/utils/village';

export interface FormValues {
  dates: {
    start: Date | null;
    end: Date | null;
  };
  quantities: {
    adults: number;
    children: number;
    infants: number;
  };
}

const schema = yup.object().shape({
  quantities: yup
    .object({
      adults: yup.number().required(),
      children: yup.number().required(),
      infants: yup.number().required(),
    })
    .required(),
  dates: yup.object().shape({
    start: yup.date().default(null).nullable(),
    end: yup.date().default(null).nullable(),
  }),
  bedrooms: yup.number().required(),
});

type Props = {
  village: Village;
  villaInPuglia: boolean;
  handleCloseModal?: () => void;
  dicts: {
    datePickerDict: IDatePickerDictonary;
    quantitiesInputDict: IQuantitiesInputDictonary;
    formDict: IContactPageFormDictionary;
    villageSideColumnDict: IVillageSideColumnDictionary;
    bookVillageDict: IBookVillageDictionary;
    enquiryDict: IEnquiryFormDictionary;
    countriesDict: ICountriesDictionary;
    modalsDict: IModalsDictionary;
  };
};

type PricesInfosProps = {
  villageSideColumnDict: IVillageSideColumnDictionary;
  village: Village;
  deals: VillageDeal[];
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function VillageSideColumnContent({
  village,
  villaInPuglia,
  handleCloseModal,
  dicts,
}: Props) {
  const lang = useLocale();
  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);
  const [datesNoLongerAvailable, setDatesNoLongerAvailable] = useState(false);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [reminderIsOpen, setReminderIsOpen] = useState(false);

  const {
    selectedDates,
    handleSelectPeriod,
    handleSelectDates,
    handleSelectPriceVariant,
    setBookSideWidgetInfos,
    extras,
    setTotalBedrooms,
    setTotalGuests,
  } = useVillageContext();

  const {
    successModalIsOpen,
    setSuccessModalIsOpen,
    optionedDates,
    setOptionedDates,
  } = useDatePickerContext();

  const { push } = useRouter();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const {
    countNights,
    smartBedroomSelect,
    getMinNights,
    isWeeklyBooking,
    getNightsToCharge,
  } = useVillage({});

  const { getDateInfos } = useVillageDates();

  const {
    errors: dictErrors,
    datePicker,
    ...dict
  } = dicts.villageSideColumnDict;

  const { quantitiesInputDict, datePickerDict, formDict } = dicts;

  const quantitiesInputOptions: QuantityOption[] = [
    { label: quantitiesInputDict.adultsLabel, name: 'adults', value: 0 },
    {
      label: quantitiesInputDict.childrenLabel,
      name: 'children',
      value: 0,
    },
    { label: quantitiesInputDict.infantsLabel, name: 'infants', value: 0 },
  ];

  const quantities = getFormQuantities();
  const dates = getFormDates();

  const {
    setValue,
    watch,
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      dates,
      quantities,
      bedrooms: 0,
    },
  });

  const quantites = watch('quantities');
  const totalGuests = quantites.adults + quantites.children;
  const totalBedrooms = watch('bedrooms');

  const bookingNigthsCount =
    selectedDates.start != null && selectedDates.end != null
      ? countNights(selectedDates)
      : null;

  const onSubmit = handleSubmit(async (data, event) => {
    if (event?.target.id != 'booking-form') {
      return;
    }

    clearErrors();
    if (optionedDates) {
      setOptionedDates(undefined);
    }
    const { adults, children, infants } = data.quantities;
    const quantitiesTotal = adults + children;

    if (selectedDates.end == null && selectedDates.start == null) {
      return setError('dates', { message: '' });
    }

    if (quantitiesTotal <= 0) {
      return setError('quantities', {
        message: dictErrors.guests,
      });
    }
    if (data.bedrooms <= 0) {
      return setError('bedrooms', {
        message: dictErrors.bedrooms,
      });
    }

    let formattedExtras: {
      villaExtra: number;
      quantity: number;
      amount: number;
    }[] = [];

    if (extras.length > 0) {
      extras.forEach(({ id, selectedQuantity, computedPrice }) => {
        formattedExtras.push({
          villaExtra: id,
          quantity: selectedQuantity,
          amount: computedPrice,
        });
      });
    }

    // Same charged end as the legacy funnel (book-villa.js chargeEndDate): weekly
    // periods round up to whole weeks, the others charge at least the min nights.
    const nightsCount = countNights(selectedDates);
    const minNights = getMinNights({ selectedDates, village, nightsCount });
    const weeklyBooking = isWeeklyBooking({
      selectedDates,
      village,
      nightsCount,
    });
    const nightsToCharge = getNightsToCharge(
      weeklyBooking,
      nightsCount,
      minNights,
    );
    // Local days of the picked dates
    const chargeEnd =
      selectedDates.start && Number.isFinite(nightsToCharge)
        ? addDays(selectedDates.start, nightsToCharge)
        : selectedDates.start;
    const dateEnd = dayKeyOrInvalid(chargeEnd);

    const countRooms = village.countRooms;

    const bedrooms = smartBedroomSelect({ countRooms, count: data.bedrooms });

    const requestData = {
      adults,
      children,
      infants,
      extras: formattedExtras,
      bedrooms,
      villa: village.id,
      dateStart: dayKeyOrInvalid(selectedDates.start),
      dateEnd,
      dateCheckOut: dayKeyOrInvalid(selectedDates.end),
    };

    if (datesNoLongerAvailable) {
      setDatesNoLongerAvailable(false);
    }

    try {
      const response = await createVillageBooking(requestData);

      if (response.error) {
        return setDatesNoLongerAvailable(true);
      }
      if (response.id) {
        setVillageSlug(village.slug);

        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: 'eventReservationFlow',
          eventCategory: 'reservation',
          eventAction: 'reservation_cta',
          eventLabel: village.title,
          language: lang,
        });

        saveBookingInLocalStorage(village.slug, response.id);
        if (lang == 'en') {
          push(`/book-villa?booking_id=${response.id}`);
        } else {
          push(`/${lang}/book-villa?booking_id=${response.id}`);
        }
      }
    } catch (error) {
      // TODO: tratar erro do loading
      console.log('ERROR:', error);
    }
  });

  function getFormDates() {
    if (searchParams.has('start') && searchParams.has('end')) {
      const now = new Date();

      // 'yyyy-MM-dd' from the URL: local midnight
      const start = parseDateString(searchParams.get('start') ?? '');

      const sixDaysAfterNow = addDays(now, 6);

      const startIsInEnabledRange = start >= sixDaysAfterNow;

      const { canCheckIn, isBusy, isOptioned } = getDateInfos({
        date: start,
        priceVariants: village.priceVariants,
        futureBookings: village.futureBookings,
      });

      const dateIsAvailable =
        canCheckIn && startIsInEnabledRange && !isBusy && !isOptioned;

      if (dateIsAvailable) {
        const dates = {
          start,
          end: parseDateString(searchParams.get('end') ?? ''),
        };

        return dates;
      }
    }

    return {
      start: null,
      end: null,
    };
  }

  function getFormQuantities() {
    let adults = 0;
    let infants = 0;
    let children = 0;
    if (searchParams.has('infants')) {
      infants = parseInt(searchParams.get('infants') || '0');
      if (infants > village.infants) {
        infants = village.infants;
      }
    }
    if (searchParams.has('adults')) {
      adults = parseInt(searchParams.get('adults') || '0');
    }
    if (searchParams.has('children')) {
      children = parseInt(searchParams.get('children') || '0');
    }
    if (adults + children > village.sleeps) {
      adults = village.sleeps;
      children = 0;
    }
    return {
      adults,
      infants,
      children,
    };
  }

  function setVillageSlug(slug: string) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('village-slug', slug);
    }
  }

  function saveBookingInLocalStorage(slug: string, bookingId: string) {
    setStorageBooking({ slug, bookingId });
  }

  function handleOnPeriodChange(
    period: VillagePricePeriod,
    variant: VillagePriceVariants,
  ) {
    handleSelectPeriod(period);
    handleSelectPriceVariant(variant);
  }

  function handleConfirmDates(value: { start: Date | null; end: Date | null }) {
    handleSelectDates(value);
    setDatesNoLongerAvailable(false);
    setBookSideWidgetInfos((prev) => ({
      ...prev,
      start: value.start,
      end: value.end,
    }));
  }

  function handleQuantitiesOnChange({
    value,
    name,
  }: {
    value: number;
    name: string;
  }) {
    setValue(`quantities.${name}` as Path<FormValues>, value);

    const nextQuantities = { ...quantites, [name]: value };
    if (nextQuantities.adults + nextQuantities.children > 0) {
      clearErrors('quantities');
    }

    setBookSideWidgetInfos((prev) => ({
      ...prev,
      quantities: {
        ...prev.quantities,
        [name]: value,
      },
    }));
  }

  function handleBedroomsOnChange(value: number) {
    setValue('bedrooms', value);
    if (value > 0) {
      clearErrors('bedrooms');
    }
  }

  function handleScrollToElement(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function handleOpenDatePicker(open: boolean) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'eventReservationFlow',
      eventCategory: 'reservation',
      eventAction: 'check_in',
      eventLabel: village.title,
      language: lang,
    });

    setDatePickerIsOpen(open);
  }

  useEffect(() => {
    const booking = getStorageBooking();
    const urlDates = getFormDates();

    if (urlDates.start && urlDates.end) {
      handleSelectDates(urlDates);
    }

    async function checkIfBookingIsActive() {
      try {
        if (booking.bookingId) {
          const response = await getVillageBooking({
            bookingId: booking.bookingId,
          });

          const isExpired = response.message === 'booking expired.';
          if (!isExpired) {
            setReminderIsOpen(true);
          } else {
            deleteStorageBooking();
          }
        }
      } catch (error) {
        console.log('Error trying to get booking');
      }
    }

    if (booking.bookingId) {
      checkIfBookingIsActive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = window.location.hash.substring(1);

    if (hash) {
      handleScrollToElement(hash);
    }
  }, []);

  useEffect(() => {
    if (totalGuests) {
      setTotalGuests(totalGuests);
    }
  }, [setTotalGuests, totalGuests]);

  useEffect(() => {
    if (totalBedrooms) {
      setTotalBedrooms(totalBedrooms);
    }
  }, [setTotalBedrooms, totalBedrooms]);

  useEffect(() => {
    if (successModalIsOpen) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    } else {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('no-scroll');
      root.classList.remove('no-scroll');
    }
  }, [successModalIsOpen]);

  return (
    <div className="h-full px-[20px]">
      <GlobalLoader isLoading={isSubmitting} />
      <BookingReminder
        modalIsOpen={reminderIsOpen}
        onOpenChange={setReminderIsOpen}
        dict={dicts.bookVillageDict}
        setVillageSlug={setVillageSlug}
      />
      {successModalIsOpen && (
        <NotifyVillageAvailabilitySuccessModal formDict={dicts.enquiryDict} />
      )}
      <div className="flex flex-col">
        <form
          id="booking-form"
          onSubmit={onSubmit}
          className="mt-auto flex flex-wrap">
          <PricesInfos
            village={village}
            villageSideColumnDict={dicts.villageSideColumnDict}
            deals={village.deals}
          />
          {bookingNigthsCount != null && (
            <div className="w-full flex justify-center items-center mb-4">
              <HalfMoonIcon className="w-8 h-8 fill-current text-gray-500 rotate-[-90deg]" />
              <span className="ml-[4px]">{`${bookingNigthsCount} ${datePickerDict.nights}`}</span>
            </div>
          )}
          <DatePicker.Root
            open={datePickerIsOpen}
            onOpenChange={handleOpenDatePicker}>
            <DatePicker.Trigger
              id={
                handleCloseModal
                  ? 'mobilde-side-column-datepicker-trigger'
                  : 'side-column-datepicker-trigger'
              }
              atVillagePage
              dict={datePickerDict}
              confirmedDates={selectedDates}
              className={cn('mb-4', {
                '[&>button]:border-error-primary': errors.dates,
              })}
            />
            <DatePicker.Content
              datePickerIsOpen={datePickerIsOpen}
              confirmedDates={selectedDates}
              confirmDates={handleConfirmDates}
              onOpenChange={setDatePickerIsOpen}
              dict={datePickerDict}
              showSummary
              summaryContainerClassname="desktop:!mt-[5vh] 2xl:!my-0"
              village={village}
              onPeriodChange={handleOnPeriodChange}
              showCalendarLegend
              extraDicts={{
                enquiryDict: dicts.enquiryDict,
                datePickerDict,
                modalsDict: dicts.modalsDict,
                countriesDict: dicts.countriesDict,
              }}
              onClearDates={() => setDatesNoLongerAvailable(false)}
            />
          </DatePicker.Root>
          <div
            className={cn('w-[calc(50%-0.5rem)] mr-auto', {
              '[&>div>div>button]:border-error-primary': errors.quantities,
            })}>
            <QuantitieInput
              dict={quantitiesInputDict}
              label={quantitiesInputDict.guestsLabel}
              id="quantities"
              register={register}
              onChange={({ value, name }) =>
                handleQuantitiesOnChange({ value, name })
              }
              options={quantitiesInputOptions}
              addedQuantities={watch('quantities')}
              guests={village.sleeps}
              infantsTotal={village.infants}
            />
          </div>
          <div
            className={cn('w-[calc(50%-0.5rem)] ml-auto mb-4', {
              '[&>div>div>div]:border-error-primary [&>div>div>div]:border':
                errors.bedrooms,
            })}>
            <QuantitieInputRoot label={dict.bedRooms} id="boxQuantities">
              <QuantitieInputBox
                onChange={handleBedroomsOnChange}
                value={watch('bedrooms')}
                max={village.bedrooms}
              />
            </QuantitieInputRoot>
          </div>
          {datesNoLongerAvailable && (
            <div className="w-full text-center mb-4">
              <span className="text-error-primary">
                {dictErrors.datesNoLongerAvailable}
              </span>
            </div>
          )}
          {errors.bedrooms && (
            <div className="w-full text-center mb-4">
              <span className="text-error-primary">
                {errors.bedrooms.message}
              </span>
            </div>
          )}
          {errors.quantities && (
            <div className="w-full text-center mb-4">
              <span className="text-error-primary">
                {errors.quantities.message}
              </span>
            </div>
          )}
          <div className="w-full flex-[0_0_auto] mb-[30px] mt-8 flex flex-wrap">
            <div
              className={cn('w-1/2 flex-[0_0_auto] flex flex-wrap', {
                'w-full': villaInPuglia,
              })}>
              <Button
                onClick={() => {
                  const enquiryForm = document.getElementById('enquiry-form');
                  if (enquiryForm) {
                    enquiryForm.scrollIntoView({ behavior: 'smooth' });
                  }
                  if (handleCloseModal) {
                    handleCloseModal();
                  }
                }}
                type="button"
                className="mr-2 bg-transparent text-primary"
                variant="unanimated">
                {villaInPuglia ? dict.requestToBook : dict.enquiry}
              </Button>
            </div>
            {!villaInPuglia && (
              <div className="w-1/2 flex-[0_0_auto] flex flex-wrap">
                <Button type="submit" variant="unanimated" className="ml-2">
                  {dict.book}{' '}
                  <span className="hidden desktop:block ml-1">{dict.now}</span>
                </Button>
              </div>
            )}
          </div>
          <VillageSideColumnShortlist
            villageSideColumnDict={dicts.villageSideColumnDict}
            village={village}
          />
          <div className="w-full mt-auto flex justify-around">
            {!villaInPuglia && (
              <div className="text-center">
                <a
                  className="text-gray-700 text-xs uppercase"
                  href={lang != 'en' ? `/${lang}/how-to-book` : `/how-to-book`}
                  title="How to book"
                  target="_blank">
                  {dict.howToBook}
                </a>
              </div>
            )}
            <VillageSideColumnEmailModal
              id={village.id}
              type={'villas'}
              villageSideColumnDict={dicts.villageSideColumnDict}
              formDict={formDict}
            />
            <div
              onClick={() => setTooltipIsOpen((prev) => !prev)}
              data-tooltip-id="share-tooltip"
              className="text-center cursor-pointer">
              <span
                className="text-gray-700 text-xs uppercase"
                title="How to book">
                {dict.share}
              </span>
            </div>
            <Tooltip
              isOpen={tooltipIsOpen}
              clickable
              style={{
                backgroundColor: 'white',
                boxShadow: '0 2px 12px 0 rgba(0,0,0,.1)',
                padding: '12px',
              }}
              border="1px solid #ebeef5"
              id="share-tooltip"
              openOnClick>
              <div className="flex justify-between gap-4">
                <a
                  target="_blank"
                  href={`https://www.facebook.com/sharer/sharer.php?u=https://www.villatravellers.com${pathname}`}>
                  <FacebookIcon className="w-4 h-4 fill-current text-gray-400 cursor-pointer" />
                </a>
                <a
                  target="_blank"
                  href={`https://twitter.com/intent/tweet?text=https://www.villatravellers.com${pathname}`}>
                  <TwitterIcon className="w-5 h-5 fill-current text-gray-400 cursor-pointer" />
                </a>
                <a
                  target="_blank"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=www.villatravellers.com${pathname}`}>
                  <LinkedinIcon className="w-4 h-4 fill-current text-gray-400 cursor-pointer" />
                </a>
              </div>
            </Tooltip>
          </div>
        </form>
      </div>
    </div>
  );
}

function PricesInfos({
  villageSideColumnDict,
  village,
  deals,
}: PricesInfosProps) {
  const lang = useLocale();
  const [bookingPrice, setBookingPrice] = useState<string>();
  const [discountInfos, setDiscountInfos] = useState<{
    value: number;
    message?: string;
  }>();
  const [isLoading, setIsLoading] = useState(false);

  const { fillIn, errors } = villageSideColumnDict;
  const { currency, applyCurrency } = useCurrency();

  const {
    selectedDates,
    selectedPeriod,
    priceVariant,
    extras,
    handleExtras,
    setVisibleDeal,
    visibleDeal,
    totalBedrooms,
    totalGuests,
  } = useVillageContext();

  const { getOriginalPrice, getVillagePrice } = useVillage({
    selectedDates,
    selectedPeriod,
    priceVariant,
    village,
  });

  const datesAreSelected = selectedDates.start && selectedDates.end;
  const canShowPrice = !!selectedPeriod && !!datesAreSelected && !!priceVariant;

  function getPrice() {
    if (canShowPrice) {
      setIsLoading(false);
      if (visibleDeal) {
        setVisibleDeal(undefined);
      }

      const { originalPrice, rawPrice, dailyPrices } = getOriginalPrice(
        village,
        selectedDates,
        totalBedrooms,
        totalGuests,
      );

      const { price, deal } = getVillagePrice(
        rawPrice,
        deals,
        dailyPrices,
        selectedDates.end,
      );

      // getVillagePrice only returns a deal that applies to this stay
      const dealCanBeApplied = deal !== null;

      if (dealCanBeApplied) {
        setVisibleDeal(deal);
        const dealTranslation = deal.deal.translation.find(
          (translation) => translation.locale === lang,
        );
        setDiscountInfos({
          value: originalPrice,
          message: dealTranslation?.title,
        });
      } else {
        setDiscountInfos(undefined);
      }

      let extrasTotal = extras
        .filter((extra) => !extra.payInVilla)
        .reduce((acc, extra) => {
          return acc + extra.computedPrice;
        }, 0);

      const totalPrice = dealCanBeApplied ? price : originalPrice;

      const formatedPrice = formatLocalizedNumber(
        applyCurrency(totalPrice + extrasTotal),
        lang,
      );

      setBookingPrice(formatedPrice);
    } else {
      setIsLoading(true);
    }
    return 0;
  }

  // useEffect(
  //   function handleBookingSideWidgetMessagesAndExtras() {
  //     if (extras.length > 0) {
  //       handleExtras(
  //         extras.map((extra) => {
  //           if (!extra.isFlatPrice) {
  //             const days = Math.max(
  //               countNights(selectedDates),
  //               extra.minNights,
  //             );
  //             const computedPrice =
  //               Math.round(Number(extra.price) * days) * extra.selectedQuantity;
  //             return { ...extra, computedPrice };
  //           }

  //           return extra;
  //         }),
  //       );
  //     }

  //     if (canShowPrice) {
  //       if (countNights(selectedDates) < selectedPeriod.minNights) {
  //         setShowError(true);
  //       } else {
  //         if (showError) {
  //           setShowError(false);
  //         }
  //         if (discountInfos) {
  //           setDiscountInfos(undefined);
  //         }
  //       }
  //     }
  //   },
  //   // eslint-disable-next-line
  //   [selectedPeriod, selectedDates, priceVariant],
  // );

  useEffect(() => {
    getPrice();
    // eslint-disable-next-line
  }, [
    extras,
    priceVariant,
    selectedDates,
    selectedPeriod,
    totalBedrooms,
    totalGuests,
  ]);

  return (
    <div
      className={cn(
        'w-full flex-[0_0_auto] h-[100px] flex flex-col flex-wrap items-center justify-center mt-[10px] text-graphite',
        {
          'mb-[10px]': !canShowPrice,
          'h-[80px]': canShowPrice && !discountInfos,
        },
      )}>
      {!canShowPrice ? (
        <h4 className="font-title text-subtitle leading-[1.25]">{fillIn}</h4>
      ) : isLoading ? (
        <>
          <RiLoader2Fill className="animate-spin size-6 fill-primary" />
        </>
      ) : (
        <>
          {discountInfos && (
            <h4 className="text-gray-500 text-xl leading-[1.25] flex gap-1">
              <span className="line-through font-title">{`${
                currency.symbol
              } ${formatLocalizedNumber(
                applyCurrency(discountInfos.value),
                lang,
              )}`}</span>
              <Tooltip
                style={{
                  zIndex: 100,
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '300px',
                }}
                clickable
                id="discount-tooltip">
                {discountInfos.message}
              </Tooltip>
              <HelpIcon
                id="discount-tooltip"
                className="w-5 h-5 fill-current text-gray-500 cursor-pointer"
              />
            </h4>
          )}
          <h2 className="text-4xl font-title">
            {`${currency.symbol} ${bookingPrice}`}
          </h2>
        </>
      )}
      {/*  {showError && (
        <p className="text-error-primary">
          {errors.theChargeIsFor}{' '}
          <span>
            {selectedPeriod?.minNights} {errors.nights}
          </span>
        </p>
      )} */}
    </div>
  );
}
